import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import React from 'react';
import { Navigate, useParams } from 'react-router';
import z from 'zod';

import { LoanMetadataEditForm } from './LoanMetadataEditForm';
import { LoansActivityTable } from './LoansActivityTable';
import { useEditLoanContext } from './useEditLoanContext';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';

// Types
const RouteParamsStruct = z.object({
  loanId: z.string({ message: 'Missing URL parameter' }),
});

const RouteBase = () => {
  const params = useParams();
  const { loanId } = RouteParamsStruct.parse(params);

  const context = useEditLoanContext();

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      gap="2rem"
      padding={dashboardRoutePadding}
    >
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Funding', path: '/' },
          { label: 'Loans', path: '/loans' },
          context.status === 'ready'
            ? {
                label:
                  context.state.initial.resolvedLoan.facility
                    .creditFacilityName,
              }
            : {
                type: 'loading',
              },
        ]}
      />
      <Flex width="100%" justify="space-between" alignItems="center">
        <Flex gap="1rem" alignItems="center">
          {(() => {
            if (context.status === 'ready') {
              return (
                <Typography variant="page-title" trim>
                  Edit{' '}
                  {
                    context.state.initial.resolvedLoan.facility
                      .creditFacilityName
                  }
                </Typography>
              );
            }
            return (
              <Typography variant="page-title" trim>
                Edit Loan
              </Typography>
            );
          })()}
          <Flex
            borderRadius="2rem"
            borderColor="gray.200"
            borderWidth="1px"
            padding="1rem"
            alignItems="center"
          >
            <Typography fontWeight="bold">Loan</Typography>
            {(() => {
              if (context.status === 'ready') {
                return (
                  <Typography>
                    {`...${loanId.substring(loanId.length - 5)}${
                      context.state.initial.resolvedLoan.externalLoanId
                        ? ` (${context.state.initial.resolvedLoan.externalLoanId})`
                        : ''
                    }`}
                  </Typography>
                );
              }
              return (
                <Typography>
                  ...{loanId.substring(loanId.length - 5)}
                </Typography>
              );
            })()}
          </Flex>
        </Flex>
        <Menu>
          <MenuButton
            as={Button}
            rightIcon={<Icon icon="chevron-down" />}
            variant="outline"
            colorScheme="v2-aurora"
          >
            Actions
          </MenuButton>
          <MenuList>
            <MenuItem type="submit">Submit for Review</MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      <LoanMetadataEditForm />
      <LoansActivityTable loanId={loanId} />
    </Flex>
  );
};

export const Route = () => {
  return (
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/loans" />;
      }}
    >
      <RouteBase />
    </RouteErrorBoundary>
  );
};
